import axios from "axios";
import {
  setAuthToken,
  getAuthToken,
  registerAxiosInstance,
} from "./utils/auth";

// Legge il valore della variabile d’ambiente definita in fase di build su Docker
const BASE_API_URL = process.env.REACT_APP_API_URL;

// chiamate anagrafica
export const APICall = axios.create({
  baseURL: `${BASE_API_URL}/api/v1/anagrafica`,
  headers: {
    "Content-Type": "application/json",
  },
});

// Registra l'istanza APICall per la gestione del token
registerAxiosInstance(APICall);

// Aggiungi l'interceptor come backup per assicurare che il token sia sempre presente
APICall.interceptors.request.use((config) => {
  const token = getAuthToken();
  // Force log in production
  window.CUSTOM_DEBUG = {
    token: token,
    headers: config.headers,
  };
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Interceptor to handle authentication errors
APICall.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      setAuthToken(null);
      window.location.href = "/";
    }
    return Promise.reject(error);
  }
);

// Gli interceptor per le richieste sono già gestiti in auth.js
const authAPICall = axios.create({
  baseURL: `${BASE_API_URL}/api/v1/auth`,
  headers: {
    "Content-Type": "application/json",
  },
});

// Registra anche l'istanza authAPICall
registerAxiosInstance(authAPICall);

// NUOVO: Istanza Axios dedicata per le API delle attività
const activityAPICall = axios.create({
  baseURL: `${BASE_API_URL}/api/v1/activity`,
  headers: {
    "Content-Type": "application/json",
  },
});

// Registra l'istanza activityAPICall per la gestione del token
registerAxiosInstance(activityAPICall);

// Interceptor per gestire errori di autenticazione
activityAPICall.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      setAuthToken(null);
      window.location.href = "/";
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  // Login: POST /auth/login
  login: async (email, password) => {
    try {
      const response = await authAPICall.post("/login", { email, password });
      // Force log in production
      window.LOGIN_DEBUG = {
        receivedToken: response.data.token,
      };
      setAuthToken(response.data.token);
      window.LOGIN_DEBUG.savedToken = getAuthToken();
      return {
        ...response.data,
        otherLoggedUsers: response.data.otherLoggedUsers || [], // return if there are other users logged in
      };
    } catch (error) {
      throw error.response?.data || { message: "Errore durante il login" };
    }
  },

  // Logout: POST /auth/logout
  logout: async () => {
    try {
      const response = await authAPICall.post("/logout");
      // remove token
      setAuthToken(null);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: "Errore durante il logout" };
    }
  },

  // Crea nuovo utente: POST /auth/create-user (solo per admin)
  createUser: async (email, password, role) => {
    try {
      const response = await authAPICall.post("/create-user", {
        email,
        password,
        role,
      });
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || {
          message: "Errore durante la creazioen dell'utente",
        }
      );
    }
  },

  // Ottieni la lista utenti: GET /auth/users (solo admin)
  getUsers: async () => {
    try {
      const response = await authAPICall.get("/users");
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || {
          message: "Errore durante il recupero degli utenti",
        }
      );
    }
  },
};

/**
 * API PER LA GESTIONE DELLO STORICO ATTIVITÀ
 *
 * Fornisce funzioni per interagire con le API del backend per il sistema
 * di tracking delle attività utente. Gestisce automaticamente i permessi
 * e la paginazione.
 */
export const activityAPI = {
  /**
   * Recupera le attività degli utenti con filtri e paginazione
   *
   * COMPORTAMENTO:
   * - Admin: vede tutte le attività di tutti gli utenti
   * - User: vede solo le proprie attività
   *
   * @param {number} page - Numero pagina (default: 1)
   * @param {number} limit - Elementi per pagina (default: 20)
   * @param {Object} filters - Filtri di ricerca
   * @param {string} filters.user_email - Email utente (solo admin)
   * @param {string} filters.action_type - Tipo azione
   * @param {string} filters.date_from - Data inizio (YYYY-MM-DD)
   * @param {string} filters.date_to - Data fine (YYYY-MM-DD)
   *
   * @returns {Promise<Object>} Risposta con attività, totale, paginazione
   */
  getActivities: async (page = 1, limit = 20, filters = {}) => {
    try {
      // Costruisce i parametri query rimuovendo valori vuoti
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, v]) => v !== "")
        ),
      });

      // CORRETTO: Usa activityAPICall invece di APICall
      const response = await activityAPICall.get(`/?${params}`);
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || {
          message: "Errore nel recupero delle attività",
        }
      );
    }
  },

  /**
   * Recupera statistiche aggregate delle attività (SOLO ADMIN)
   *
   * Fornisce dati statistici degli ultimi 30 giorni per dashboard
   * amministrative e analisi dei pattern di utilizzo.
   *
   * @returns {Promise<Object>} Statistiche aggregate per tipo azione e data
   */
  getStats: async () => {
    try {
      // CORRETTO: Usa activityAPICall invece di APICall
      const response = await activityAPICall.get("/stats");
      return response.data;
    } catch (error) {
      throw (
        error.response?.data || {
          message: "Errore nel recupero delle statistiche",
        }
      );
    }
  },
};
